import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';

//* HOC'S
import { withLanguageContext, withUserContext, withUIContext } from 'context';

//* Helpers
import api from 'helpers/_api';

//* Components
import Text from 'components/common/Text';
import Page from 'components/common/Page';
import FormContainer from 'components/common/FormContainer';
import FormItem from 'components/common/FormItem';
import FormInput from 'components/common/FormInput';
import Button from 'components/common/Button';
import Logo from 'components/common/Logo';
import Icon from 'components/common/Icon';
import LoginPopup from 'components/common/Popups/LoginPopup';

//* Style
import ResetPasswordStyle from 'styles/pages/ResetPasswordStyle';

const ResetPassword = ({ translate, resetPassword, openPopup, forgotPassword, ...props }) => {
    //! Router
    const router = useRouter();

    //! States
    const [isSuccess, setIsSuccess] = useState(false);
    const [isResendSuccess, setIsResendSuccess] = useState(false);

    const onFinish = useCallback((values) => {
        setIsSuccess('loading');

        return resetPassword(values)
            .then(() => setIsSuccess(true))
            .catch((err) => {
                setIsSuccess(false);

                return Promise.reject(err);
            });
    }, []);

    const handleResendButton = useCallback(() => {
        setIsResendSuccess('loading');

        return forgotPassword({ email: router.query.email })
            .then(() => {
                setIsResendSuccess(true);

                setTimeout(() => {
                    setIsResendSuccess(false);
                }, 3000);
            })
            .catch((err) => {
                setIsResendSuccess(false);

                return Promise.reject(err);
            });
    }, [router]);

    return (
        <Page
            nextSeoProps={{
                noindex: true,
                nofollow: true
            }}
            hrefLang={false}
        >
            <ResetPasswordStyle>
                <Logo className={'reset-password-logo'} />
                {props.expired ? (
                    <div className='resend-message'>
                        <Text
                            tag={'h2'}
                            className={'expired-changed-title font-soundscape-bold'}
                            text={'expired'}
                        />
                        <Text
                            tag={'h6'}
                            className={'expired-changed-subtitle font-ping-medium'}
                            text={'expiredDescription'}
                        />
                        <Button
                            className={'resend-button'}
                            onClick={() => handleResendButton()}
                            text={'resendPassword'}
                            type={'icon-aside'}
                            disabled={isSuccess === 'loading'}
                            isBtnLoad={isSuccess === 'loading'}
                        />

                        {isResendSuccess && isResendSuccess !== 'loading' ? (
                            <Text
                                className={'resend-text font-ping-medium pXS successColor'}
                                text={'resendSuccess'}
                            />
                        ) : null}
                    </div>
                ) : !isSuccess ? (
                    <div className='content-wrap'>
                        <Text
                            className={'h2 font-soundscape-bold reset-password-title'}
                            text={'resetPasswordTitle'}
                        />
                        <Text
                            className={'h6 font-ping-medium reset-password-subtitle'}
                            text={'resetPasswordSubtitle'}
                        />
                        <FormContainer
                            onFinish={onFinish}
                            initialValues={{
                                email: props.email,
                                token: props.token,
                            }}>
                            <FormItem
                                name={'password'}
                                validationType={'password'}
                                validateWith={'password_confirmation'}>
                                <FormInput
                                    placeholder={translate('newPassword')}
                                    type={'password'}
                                />
                            </FormItem>

                            <FormItem
                                name={'password_confirmation'}
                                validationType={'c_password'}>
                                <FormInput
                                    placeholder={translate('confirmNewPassword')}
                                    type={'password'}
                                />
                            </FormItem>

                            <FormItem
                                name={'token'}
                                noStyle={true}>
                                <FormInput type={'hidden'} />
                            </FormItem>

                            <FormItem
                                name={'email'}
                                noStyle={true}>
                                <FormInput type={'hidden'} />
                            </FormItem>

                            <div className={'form-lower-wrap'}>
                                <Button
                                    text={'saveChanges'}
                                    type={'icon-aside'}
                                    disabled={isSuccess === 'loading'}
                                    isBtnLoad={isSuccess === 'loading'}
                                />
                            </div>
                        </FormContainer>
                    </div>
                ) : (
                    <div className='success-message'>
                        <div className='circle successColor-bg'>
                            <Icon
                                name={'check'}
                                className={'success-icon'}
                            />
                        </div>
                        <Text
                            tag={'h2'}
                            className={'password-changed-title font-soundscape-bold'}
                            text={'passwordChangedTitle'}
                        />
                        <Text
                            tag={'h6'}
                            className={'password-changed-subtitle font-ping-medium'}
                            text={'passwordChangedSubtitle'}
                        />
                        <Button
                            className={'login-button'}
                            onClick={() => openPopup(<LoginPopup />, { isReset: true })}
                            text={'login'}
                            type={'icon-aside'}
                        />
                    </div>
                )}
            </ResetPasswordStyle>
        </Page>
    );
};

export async function getServerSideProps({ req, locale, res }) {
    if (req.query.token && req.query.email) {
        return await api
            .post('check-password-token', { token: req.query.token, email: req.query.email }, locale)
            .then((resp) => {
                return {
                    props: {
                        token: req.query.token,
                        email: req.query.email,
                        expired: false,
                    },
                };
            })
            .catch((err) => {
                if (err.response.status === 419) {
                    return {
                        props: {
                            expired: true,
                        },
                    };
                }
                return {
                    notFound: true,
                };
            });
    }
    return {
        notFound: true,
    };
}

export default withUIContext(withUserContext(withLanguageContext(ResetPassword, ['translate']), ['resetPassword', 'forgotPassword']), ['openPopup']);
